import jp from 'jsonpath';
import type {
  EntityDetailSchema,
  ConfigEntityDetail,
} from '@web-config-app/core';
import {
  transformIdWithTenant,
  transformDomainEntityIdWithTenant,
} from '@web-config-app/core';
import {
  convertSchemaPathToJsonPath,
  findSchemaPathsWhere,
  hasEntityImportAnnotation,
} from '@web-config-app/schema-utils';
import {
  getEntityReferenceIds,
  isLinkedEntityRef,
  isRelationshipEntityRef,
} from '../../utilities/get-entity-reference-ids/get-entity-reference-ids';

/**
 * Asset entities (images/pdfs) are special-cased for now: we do NOT rewrite their root domainEntityId
 * and we also avoid rewriting any linked refs that point at these entity types.
 *
 * This effectively excludes `x-entity-asset` references from domainEntityId rewriting until we
 * explicitly decide to support asset ID transformation.
 *
 * TODO:: potentially move this to the entity config
 */
const DOMAIN_ENTITY_ID_TRANSFORM_SKIP_TYPES = new Set([
  'config-image',
  'config-pdf',
]);

/**
 * Transforms an entity ID if it's in the set of IDs that need transformation.
 */
export const transformIdIfNeeded = async (
  id: string,
  idsToTransform: Set<string>,
  tenantId: string,
): Promise<string> => {
  if (!idsToTransform.has(id)) return id;
  return transformIdWithTenant(id, tenantId);
};

/**
 * Applies ID transformations to entity data.
 *
 * Handles three locations where IDs appear:
 * 1. The root entity ID (`data.id`) - always transformed
 * 2. Relationship IDs in entity data fields (e.g., `activityId` with x-entity-reference.relationship)
 * 3. Relationship IDs in the JSON:API `relationships` object
 *
 * Relationship IDs are only transformed if they're in the idsToTransform set
 * (i.e., entities that don't exist in the target tenant and need new IDs).
 */
export const applyIdTransformations = async (
  data: ConfigEntityDetail,
  relationshipIdsToTransform: Set<string>,
  schema: EntityDetailSchema,
  tenantId: string,
  linkedEntityExists: (
    domainEntityType: string,
    domainEntityId: string,
  ) => Promise<boolean>,
): Promise<ConfigEntityDetail> => {
  /**
   * NOTE on `linkedEntityExists` (required):
   * Linked refs (schema fields annotated with `x-entity-reference.domainEntityType`) are domainEntityIds
   * that may or may not already exist in the target tenant.
   *
   * We use this callback to avoid rewriting linked refs that already exist in the target under the
   * *original* ID (rewriting would break the link). When the original does NOT exist, we rewrite to
   * the deterministic tenant-transformed ID.
   */
  // 1. Always transform root ID (caller verified entity doesn't exist in target)
  const transformedRootId = await transformIdWithTenant(data.id, tenantId);

  // Also transform domainEntityId deterministically for the target tenant (format-preserving).
  const currentDomainEntityType =
    data.attributes?.entityMetadata?.domainEntityType;
  const originalDomainEntityId =
    data.attributes?.entityMetadata?.domainEntityId;
  if (
    typeof originalDomainEntityId === 'string' &&
    (typeof currentDomainEntityType !== 'string' ||
      currentDomainEntityType.trim().length === 0)
  ) {
    // We only enforce this when domainEntityId is present. Otherwise, the entity might legitimately
    // be missing metadata in some flows (and we won't attempt to transform/validate anyway).
    throw new Error(
      'applyIdTransformations: domainEntityType is required when domainEntityId is present',
    );
  }
  const shouldTransformRootDomainEntityId =
    typeof originalDomainEntityId === 'string' &&
    typeof currentDomainEntityType === 'string' &&
    !DOMAIN_ENTITY_ID_TRANSFORM_SKIP_TYPES.has(currentDomainEntityType);
  let transformedDomainEntityId: string | undefined;
  if (shouldTransformRootDomainEntityId) {
    transformedDomainEntityId = await transformDomainEntityIdWithTenant(
      originalDomainEntityId,
      tenantId,
    );
  }

  const rootIdAndMetadataTransformed: ConfigEntityDetail = {
    ...data,
    id: transformedRootId,
    attributes: data.attributes?.entityMetadata
      ? {
          ...data.attributes,
          entityMetadata: {
            ...data.attributes.entityMetadata,
            ...(shouldTransformRootDomainEntityId && {
              domainEntityId: transformedDomainEntityId,
            }),
          },
        }
      : data.attributes,
  };

  /**
   * Find any schema fields that need embedded domainEntityId transformation for import.
   *
   * Example value: `urn:league:<tenant>:<system>:<domainEntityId>`
   */
  const questionnaireIdInActivitiesPaths = Array.from(
    findSchemaPathsWhere(
      schema,
      hasEntityImportAnnotation('questionnaireIdInActivities'),
    ),
  ).map((schemaPath) => convertSchemaPathToJsonPath(schemaPath));

  /**
   * Extract references from the *original* data, before we rewrite any IDs.
   *
   * `getEntityReferenceIds` returns both JSONPaths and the current values at those paths.
   * We want linkedEntity existence checks + transformations to be based on the *original* IDs,
   * and then apply changes onto `transformedData` via JSONPath writes.
   */
  const allRefs = getEntityReferenceIds(data, schema);
  const relationshipRefs = allRefs.filter(isRelationshipEntityRef);
  const linkedRefs = allRefs.filter(isLinkedEntityRef);

  // If there are no relationship IDs to transform and no schema-discovered references,
  // return the root+metadata transformation without doing a deep clone / JSONPath writes.
  if (
    relationshipIdsToTransform.size === 0 &&
    relationshipRefs.length === 0 &&
    linkedRefs.length === 0 &&
    questionnaireIdInActivitiesPaths.length === 0
  ) {
    return rootIdAndMetadataTransformed;
  }

  // Deep copy before in-place JSONPath updates. We do this even when there are no relationship IDs
  // to transform because linked references (x-entity-reference.domainEntityType) still need updates.
  const transformedData: ConfigEntityDetail = JSON.parse(
    JSON.stringify(rootIdAndMetadataTransformed),
  );

  // 2. Transform IDs in relationship reference fields (e.g., activityId: 'xxx')
  //    These are fields marked with x-entity-reference: { relationship: 'activity' } in the schema

  await Promise.all(
    relationshipRefs.map(async ({ jsonPath, configEntityId }) => {
      const newConfigEntityId = await transformIdIfNeeded(
        configEntityId,
        relationshipIdsToTransform,
        tenantId,
      );
      jp.value(transformedData, jsonPath, newConfigEntityId);
    }),
  );

  // 2b. Transform IDs in linked entity reference fields (e.g., relatedProductId: 'xxx')
  //     These are fields marked with x-entity-reference: { domainEntityType: 'config-activity' } in the schema
  await Promise.all(
    linkedRefs
      .filter(
        ({ domainEntityType }) =>
          !DOMAIN_ENTITY_ID_TRANSFORM_SKIP_TYPES.has(domainEntityType),
      )
      .map(async ({ jsonPath, domainEntityId, domainEntityType }) => {
        /**
         * IMPORTANT: Linked refs are domainEntityIds.
         *
         * If the referenced entity already exists in the target tenant under the *original* ID,
         * rewriting the ID would break the link. So we only rewrite when we believe the original
         * does NOT exist in the target.
         *
         * This also supports the "import dependencies first" workflow:
         * - if the dependency was previously imported and had its ID transformed, the original ID
         *   will not exist in the target, so we will rewrite to the deterministic transformed ID.
         *
         * If `linkedEntityExists` is not provided, we cannot check existence and will always rewrite.
         */
        const existsOnTarget = await linkedEntityExists(
          domainEntityType,
          domainEntityId,
        );
        if (existsOnTarget) return;

        const newDomainEntityId = await transformDomainEntityIdWithTenant(
          domainEntityId,
          tenantId,
        );
        jp.value(transformedData, jsonPath, newDomainEntityId);
      }),
  );

  // 2c. Transform embedded questionnaire domainEntityIds in imported activities
  const transformConfigQuestionnaireEntityIdInImportedActivity = async (
    value: string,
  ): Promise<string> => {
    /**
     * Expected form: urn:league:<tenant>:<system>:<id>
     * We only transform the trailing `<id>`.
     */
    const parts = value.split(':');
    if (parts.length < 5) return value;
    if (parts[0] !== 'urn' || parts[1] !== 'league') return value;

    const trailingId = parts[parts.length - 1];
    if (!trailingId) return value;

    /**
     * `questionnaireIdInActivities` is treated as a Questionnaire ID in all cases.
     *
     * If the questionnaire already exists in the target tenant under the *original* ID,
     * rewriting would break the link, so we keep the URN unchanged.
     */
    const existsOnTarget = await linkedEntityExists(
      'questionnaire-config',
      trailingId,
    );
    if (existsOnTarget) return value;

    const transformedId = await transformDomainEntityIdWithTenant(
      trailingId,
      tenantId,
    );
    if (transformedId === trailingId) return value;

    const next = [...parts];
    next[next.length - 1] = transformedId;
    return next.join(':');
  };

  await Promise.all(
    questionnaireIdInActivitiesPaths.map(async (jsonPath) => {
      const nodes = jp.nodes(transformedData, jsonPath);
      await Promise.all(
        nodes.map(async ({ path: nodePath, value }) => {
          if (typeof value !== 'string' || value.length === 0) return;
          const transformedValue =
            await transformConfigQuestionnaireEntityIdInImportedActivity(value);
          if (transformedValue === value) return;
          jp.value(transformedData, jp.stringify(nodePath), transformedValue);
        }),
      );
    }),
  );

  // 3. Transform IDs in the relationships object (e.g., relationships.activity.data.id)
  //    Structure: { activity: { data: { id: 'xxx', type: 'config-activity' } } }
  //    Or array:  { activities: { data: [{ id: 'a', type: 'config-activity' }, { id: 'b', type: 'config-activity' }] } }
  if (transformedData.relationships) {
    const entries = await Promise.all(
      Object.entries(transformedData.relationships).map(
        async ([name, relationship]) => {
          if (!relationship.data) return [name, relationship];

          // Handle array and single entity cases separately
          if (Array.isArray(relationship.data)) {
            const transformedEntities = await Promise.all(
              relationship.data.map(async (entity) => ({
                ...entity,
                id: await transformIdIfNeeded(
                  entity.id,
                  relationshipIdsToTransform,
                  tenantId,
                ),
              })),
            );
            return [name, { ...relationship, data: transformedEntities }];
          }

          const transformedEntity = {
            ...relationship.data,
            id: await transformIdIfNeeded(
              relationship.data.id,
              relationshipIdsToTransform,
              tenantId,
            ),
          };
          return [name, { ...relationship, data: transformedEntity }];
        },
      ),
    );

    transformedData.relationships = Object.fromEntries(entries);
  }

  return transformedData;
};
